import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiArrowULeftTop } from "@mdi/js";
import { Typography, useTheme } from "@mui/material";

const undoCancelSchema = z.object({
  reason: z.string().min(1, "Reason is required."),
});

type UndoCancelFormValues = z.infer<typeof undoCancelSchema>;

interface UndoCancelModalProps {
  open: boolean;
  onClose: () => void;
  onUndo: (data: UndoCancelFormValues) => Promise<void>;
  isLoading?: boolean;
}

export const UndoCancelModal = ({
  open,
  onClose,
  onUndo,
  isLoading = false,
}: UndoCancelModalProps) => {
  const theme = useTheme();

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<UndoCancelFormValues>({
    mode: "onChange",
    defaultValues: {
      reason: "",
    },
    resolver: zodResolver(undoCancelSchema),
  });

  const undoCancelFields = useMemo(
    () => [
      {
        label: "Reason",
        name: "reason",
        required: true,
        multiline: true,
        rows: 2,
        formControl: control,
      },
    ],
    [control]
  );

  const handleFormSubmit = async (data: UndoCancelFormValues) => {
    await onUndo(data);
    handleClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      width={480}
      titleIcon={mdiArrowULeftTop}
      leftIconColor={theme.palette.primary.main}
      title="Undo Cancel"
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: "Undo Cancel",
          variant: BUTTON_VARIANTS.CONTAINED,
          disabled: !isValid || !isDirty || isLoading,
          onClick: handleSubmit(handleFormSubmit),
        },
      ]}
    >
      <Typography
        variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
        color="grey"
        mb={2}
        px={2}
        fontSize={theme.fontSize.medium}
      >
        Undoing this cancellation will move the Order back to the status it was
        in before it was cancelled.
      </Typography>
      <MiDetailFields detailFields={undoCancelFields} />
    </MiModal>
  );
};
